// ============================================================================
// XTRACKER SAMPLE EDITOR
// Uses FFmpeg WASM for audio effects (same engine as wavedit)
// ============================================================================
const { renderWaveform } = require('@azopsoft/shared/ui');

// Lazy-load FFmpeg only when an effect is first used
let FFmpegClass = null;
let fetchFileFn = null;

// ---- FFmpeg WASM initialization (lazy-loaded) ----
let ffmpegInstance = null;
let ffmpegReady = false;
let ffmpegLoading = false;

async function initFFmpeg() {
   if (ffmpegReady || ffmpegLoading) return;
   ffmpegLoading = true;
   try {
      // Dynamic import — only loads FFmpeg WASM when first effect is used
      const ffmpegMod = await import('@ffmpeg/ffmpeg');
      const utilMod = await import('@ffmpeg/util');
      FFmpegClass = ffmpegMod.FFmpeg;
      fetchFileFn = utilMod.fetchFile;
      ffmpegInstance = new FFmpegClass();
      const baseURL = '/ffmpeg';
      await ffmpegInstance.load({
         coreURL: `${baseURL}/ffmpeg-core.module.js`,
         wasmURL: `${baseURL}/ffmpeg-core.wasm`,
      });
      ffmpegReady = true;
   } catch (err) {
      console.error('[xtracker] FFmpeg init failed:', err);
   }
   ffmpegLoading = false;
}

// Convert AudioBuffer to WAV Blob for FFmpeg input
function audioBufferToWavBlob(buffer) {
   const wav = audioBufferToWavInternal(buffer);
   return new Blob([new Uint8Array(wav)], { type: 'audio/wav' });
}

// Run an FFmpeg audio filter on an AudioBuffer, returns processed AudioBuffer
async function ffFilter(buffer, filterString) {
   if (!ffmpegReady) await initFFmpeg();
   if (!ffmpegInstance) throw new Error('FFmpeg not available');

   const inputBlob = audioBufferToWavBlob(buffer);
   const inputUrl = URL.createObjectURL(inputBlob);

   try {
      await ffmpegInstance.deleteFile('input.wav').catch(() => {});
      await ffmpegInstance.deleteFile('output.wav').catch(() => {});
      await ffmpegInstance.writeFile('input.wav', await fetchFileFn(inputUrl));

      const cli = ['-y', '-i', 'input.wav', '-filter:a', String(filterString),
         '-ar', String(buffer.sampleRate), '-ac', String(buffer.numberOfChannels),
         '-c:a', 'pcm_s16le', '-f', 'wav', 'output.wav'];
      await ffmpegInstance.exec(cli);

      const data = await ffmpegInstance.readFile('output.wav');
      if (!data || data.length === 0) throw new Error('FFmpeg produced no output');

      const outBlob = new Blob([data.buffer], { type: 'audio/wav' });
      const outUrl = URL.createObjectURL(outBlob);
      const resp = await fetch(outUrl);
      const arrBuf = await resp.arrayBuffer();
      const processed = await audioCtx.decodeAudioData(arrBuf.slice(0));
      URL.revokeObjectURL(outUrl);
      return processed;
   } finally {
      URL.revokeObjectURL(inputUrl);
   }
}

// Apply FFmpeg filter to selection or whole buffer, then commit
async function applyFFEffect(filterString) {
   if (!audioBuffer) return;
   const range = getSelectionRange();
   let result;
   if (range) {
      // Extract selection, process, splice back
      const selLen = range.end - range.start;
      const selBuf = audioCtx.createBuffer(audioBuffer.numberOfChannels, selLen, audioBuffer.sampleRate);
      for (let c = 0; c < audioBuffer.numberOfChannels; c++) {
         const src = audioBuffer.getChannelData(c);
         const dst = selBuf.getChannelData(c);
         for (let i = 0; i < selLen; i++) dst[i] = src[range.start + i];
      }
      const processed = await ffFilter(selBuf, filterString);
      // Splice back — processed may have different length
      const newLen = audioBuffer.length - selLen + processed.length;
      const newBuf = audioCtx.createBuffer(audioBuffer.numberOfChannels, newLen, audioBuffer.sampleRate);
      for (let c = 0; c < audioBuffer.numberOfChannels; c++) {
         const src = audioBuffer.getChannelData(c);
         const dst = newBuf.getChannelData(c);
         // Copy before selection
         for (let i = 0; i < range.start; i++) dst[i] = src[i];
         // Copy processed
         const pdata = processed.getChannelData(c);
         for (let i = 0; i < processed.length; i++) dst[range.start + i] = pdata[i];
         // Copy after selection
         for (let i = 0; i < audioBuffer.length - range.end; i++) dst[range.start + processed.length + i] = src[range.end + i];
      }
      result = newBuf;
   } else {
      result = await ffFilter(audioBuffer, filterString);
   }
   commitBuffer(result);
}

// ---- State ----
let canvas = null;
let ctx = null;
let currentSample = null;      // reference to the engine sample object
let audioBuffer = null;        // AudioBuffer version for rendering/editing
let selection = null;          // { start, end } in samples
let loopDrag = null;           // 'start' | 'end' | null
let isSelecting = false;
let playNode = null;
let audioCtx = null;

// ---- Playhead tracking (engine playback + preview) ----
// Each entry: { id, startTime, playbackRate, offsetSamples, source, isPreview }
let activePlayheads = [];
let playheadRaf = null;
let playheadIdCounter = 0;
// Cached waveform frame (offscreen canvas) — avoids re-rendering waveform 60x/sec
let waveformCache = null;     // HTMLCanvasElement
let waveformCacheDirty = true;

// ---- Helpers: convert between engine sample (float array) and AudioBuffer ----
function sampleToAudioBuffer(sample, ctx) {
   if (!sample || !sample.data || !sample.data.length) return null;
   const sr = sample.sampleRate || ctx.sampleRate || 44100;
   const buf = ctx.createBuffer(1, sample.data.length, sr);
   const ch = buf.getChannelData(0);
   for (let i = 0; i < sample.data.length; i++) ch[i] = sample.data[i];
   return buf;
}

function audioBufferToSampleData(buffer) {
   const channels = buffer.numberOfChannels;
   const len = buffer.length;
   const out = new Float32Array(len);
   for (let c = 0; c < channels; c++) {
      const ch = buffer.getChannelData(c);
      for (let i = 0; i < len; i++) out[i] += ch[i] / channels;
   }
   return Array.from(out);
}

// ---- Init ----
export function initSampleEditor(ctxRef) {
   audioCtx = ctxRef;
   // FFmpeg is lazy-loaded on first effect use via initFFmpeg()
   canvas = document.getElementById('xt-se-wave');
   if (canvas) {
      ctx = canvas.getContext('2d');
      wireCanvas();
      // Redraw on container resize
      if (window.ResizeObserver) {
         new ResizeObserver(() => { if (currentSample) drawWaveform(); else drawEmpty(); }).observe(canvas);
      }
      window.addEventListener('resize', () => { if (currentSample) drawWaveform(); else drawEmpty(); });
   }
   wireToolbar();
}

// ---- Load a sample into the editor ----
export function loadSample(sample) {
   currentSample = sample;
   selection = null;
   // Clear engine playheads when switching instrument/sample
   activePlayheads = activePlayheads.filter(p => p.isPreview);
   if (activePlayheads.length === 0) stopPlayheadRaf();
   if (!sample || !sample.data || !sample.data.length) {
      audioBuffer = null;
      drawEmpty();
      return;
   }
   audioBuffer = sampleToAudioBuffer(sample, audioCtx);
   drawWaveform();
}

// ---- Drawing ----
function drawEmpty() {
   if (!canvas || !ctx) return;
   resizeCanvas();
   waveformCache = null;
   const tc = window.themeColors || [];
   ctx.fillStyle = (tc[0] || '#0a0a0a');
   ctx.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight);
   ctx.fillStyle = (tc[6] || '#555');
   ctx.font = '11px monospace';
   ctx.textAlign = 'center';
   ctx.fillText('— no sample —', canvas.clientWidth / 2, canvas.clientHeight / 2);
}

async function drawWaveform() {
   if (!canvas || !audioBuffer) { drawEmpty(); return; }
   resizeCanvas();
   const tc = window.themeColors || [];
   const w = canvas.clientWidth;
   const h = canvas.clientHeight;
   // Use shared renderWaveform for the base waveform
   try {
      const rendered = await renderWaveform(audioBuffer, {
         channel: 0,
         backgroundColor: tc[0] || '#0a0a0a',
         waveformColor: tc[14] || '#cccccc',
      }, w, h, 1);
      ctx.drawImage(rendered, 0, 0, w, h);
   } catch (e) {
      // Fallback: manual draw
      manualDraw(w, h);
   }
   drawOverlay(w, h);
   // Cache the rendered frame for playhead overlay (avoids re-rendering 60x/sec)
   cacheWaveformFrame();
}

function cacheWaveformFrame() {
   if (!canvas || !audioBuffer) { waveformCache = null; return; }
   const w = canvas.width;
   const h = canvas.height;
   if (!waveformCache) waveformCache = document.createElement('canvas');
   if (waveformCache.width !== w || waveformCache.height !== h) {
      waveformCache.width = w;
      waveformCache.height = h;
   }
   const cctx = waveformCache.getContext('2d');
   cctx.drawImage(canvas, 0, 0);
}

function manualDraw(w, h) {
   const tc = window.themeColors || [];
   ctx.fillStyle = tc[0] || '#0a0a0a';
   ctx.fillRect(0, 0, w, h);
   if (!audioBuffer) return;
   const data = audioBuffer.getChannelData(0);
   const len = data.length;
   const step = Math.max(1, Math.floor(len / w));
   ctx.strokeStyle = tc[14] || '#cccccc';
   ctx.lineWidth = 1;
   ctx.beginPath();
   for (let x = 0; x < w; x++) {
      let min = 1, max = -1;
      for (let i = 0; i < step; i++) {
         const v = data[x * step + i] || 0;
         if (v < min) min = v;
         if (v > max) max = v;
      }
      const yMin = h / 2 - max * (h / 2 - 2);
      const yMax = h / 2 - min * (h / 2 - 2);
      if (x === 0) ctx.moveTo(x, yMin);
      else ctx.lineTo(x, yMin);
      ctx.lineTo(x, yMax);
   }
   ctx.stroke();
}

function drawOverlay(w, h) {
   const tc = window.themeColors || [];
   const len = audioBuffer ? audioBuffer.length : 0;
   if (!len) return;
   // Helper: convert hex color + alpha to rgba string
   const hexA = (hex, a) => {
      if (!hex || !hex.startsWith('#')) return `rgba(136,136,136,${a})`;
      const r = parseInt(hex.slice(1, 3), 16) || 0;
      const g = parseInt(hex.slice(3, 5), 16) || 0;
      const b = parseInt(hex.slice(5, 7), 16) || 0;
      return `rgba(${r},${g},${b},${a})`;
   };
   // Selection highlight — use tc[11] (cb accent)
   if (selection) {
      const x0 = Math.min(selection.start, selection.end) / len * w;
      const x1 = Math.max(selection.start, selection.end) / len * w;
      ctx.fillStyle = hexA(tc[11], 0.2);
      ctx.fillRect(x0, 0, x1 - x0, h);
      ctx.strokeStyle = tc[11] || '#a5a5a5';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x0, 0); ctx.lineTo(x0, h);
      ctx.moveTo(x1, 0); ctx.lineTo(x1, h);
      ctx.stroke();
   }
   // Loop region — use tc[9] (c9) for distinction
   if (currentSample && currentSample.loop && currentSample.loop.enabled && currentSample.loop.length) {
      const ls = currentSample.loop.start / len * w;
      const ll = currentSample.loop.length / len * w;
      ctx.strokeStyle = tc[9] || '#878787';
      ctx.setLineDash([4, 3]);
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(ls, 0); ctx.lineTo(ls, h);
      ctx.moveTo(ls + ll, 0); ctx.lineTo(ls + ll, h);
      ctx.stroke();
      ctx.setLineDash([]);
      // Loop region tint
      ctx.fillStyle = hexA(tc[9], 0.13);
      ctx.fillRect(ls, 0, ll, h);
   }
   // Center line — use tc[4] (c4)
   ctx.strokeStyle = tc[4] || '#3c3c3c';
   ctx.lineWidth = 0.5;
   ctx.beginPath();
   ctx.moveTo(0, h / 2); ctx.lineTo(w, h / 2);
   ctx.stroke();
}

function resizeCanvas() {
   if (!canvas || !ctx) return;
   const dpr = window.devicePixelRatio || 1;
   const w = canvas.clientWidth;
   const h = canvas.clientHeight;
   if (w < 10 || h < 10) return;
   const targetW = Math.round(w * dpr);
   const targetH = Math.round(h * dpr);
   if (canvas.width !== targetW || canvas.height !== targetH) {
      canvas.width = targetW;
      canvas.height = targetH;
   }
   ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

// ---- Canvas mouse interaction: selection + loop drag ----
function wireCanvas() {
   canvas.addEventListener('mousedown', (e) => {
      if (!audioBuffer) return;
      if (e.button !== 0) return; // only left button starts selection
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const w = canvas.clientWidth;
      const samplePos = Math.floor(x / w * audioBuffer.length);
      // Check if clicking near loop markers
      if (currentSample && currentSample.loop && currentSample.loop.enabled) {
         const ls = currentSample.loop.start / audioBuffer.length * w;
         const ll = currentSample.loop.length / audioBuffer.length * w;
         if (Math.abs(x - ls) < 5) { loopDrag = 'start'; return; }
         if (Math.abs(x - (ls + ll)) < 5) { loopDrag = 'end'; return; }
      }
      // Start selection
      isSelecting = true;
      selection = { start: samplePos, end: samplePos };
      drawWaveform();
   });

   canvas.addEventListener('mousemove', (e) => {
      if (!audioBuffer) return;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const w = canvas.clientWidth;
      const samplePos = Math.floor(x / w * audioBuffer.length);
      if (loopDrag === 'start') {
         if (currentSample.loop) {
            currentSample.loop.start = Math.max(0, Math.min(samplePos, currentSample.loop.start + currentSample.loop.length - 1));
            currentSample.loop.length = (currentSample.loop.start + currentSample.loop.length) - currentSample.loop.start;
            syncLoopUI();
            drawWaveform();
         }
         return;
      }
      if (loopDrag === 'end') {
         if (currentSample.loop) {
            const end = Math.max(currentSample.loop.start + 1, samplePos);
            currentSample.loop.length = end - currentSample.loop.start;
            syncLoopUI();
            drawWaveform();
         }
         return;
      }
      if (isSelecting) {
         selection.end = Math.max(0, Math.min(samplePos, audioBuffer.length - 1));
         drawWaveform();
      }
   });

   document.addEventListener('mouseup', () => {
      isSelecting = false;
      loopDrag = null;
   });

   // Double-click: clear selection
   canvas.addEventListener('dblclick', () => {
      selection = null;
      drawWaveform();
   });

   // Right-click: context menu (with "Clear selection" option inside)
   canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      showContextMenu(e.clientX, e.clientY);
   });
}

function syncLoopUI() {
   const ls = document.getElementById('xt-se-loopstart');
   const ll = document.getElementById('xt-se-looplen');
   const lc = document.getElementById('xt-se-loop');
   if (currentSample && currentSample.loop) {
      if (ls) ls.value = currentSample.loop.start;
      if (ll) ll.value = currentSample.loop.length;
      if (lc) lc.checked = !!currentSample.loop.enabled;
   }
}

// ---- Playhead tracking ----
// Called by xtracker.js on EVENT.samplePlay when the engine triggers a sample
// that matches the currently-edited instrument.
export function onEngineSamplePlay(result) {
   if (!audioBuffer || !audioCtx || !result || !result.source) return;
   const loop = currentSample && currentSample.loop;
   const ph = {
      id: ++playheadIdCounter,
      startTime: audioCtx.currentTime,
      playbackRate: result.startPlaybackRate || 1,
      offsetSamples: (result.effects && result.effects.offset ? result.effects.offset.value : 0) || 0,
      source: result.source,
      isPreview: false,
      loopEnabled: !!(loop && loop.enabled && loop.length > 2),
      loopStart: loop ? loop.start : 0,
      loopEnd: loop ? (loop.start + loop.length) : 0,
   };
   // Clean up when the engine source ends
   if (result.source.onended !== undefined) {
      const origOnEnded = result.source.onended;
      result.source.onended = function () {
         removePlayhead(ph.id);
         if (typeof origOnEnded === 'function') origOnEnded.apply(this, arguments);
      };
   }
   activePlayheads.push(ph);
   startPlayheadRaf();
}

function removePlayhead(id) {
   activePlayheads = activePlayheads.filter(p => p.id !== id);
   if (activePlayheads.length === 0) stopPlayheadRaf();
}

function stopPlayheadRaf() {
   if (playheadRaf) {
      cancelAnimationFrame(playheadRaf);
      playheadRaf = null;
   }
   // Final redraw without playheads
   drawWaveform();
}

function startPlayheadRaf() {
   if (playheadRaf) return;
   const tick = () => {
      if (!activePlayheads.length) { stopPlayheadRaf(); return; }
      drawPlayheads();
      playheadRaf = requestAnimationFrame(tick);
   };
   playheadRaf = requestAnimationFrame(tick);
}

// Blit cached waveform frame and draw playhead lines on top (cheap — no re-render)
function drawPlayheads() {
   if (!canvas || !ctx || !audioBuffer) return;
   const w = canvas.clientWidth;
   const h = canvas.clientHeight;
   // Blit cached waveform frame
   if (waveformCache) {
      ctx.drawImage(waveformCache, 0, 0, w, h);
   } else {
      // No cache yet — trigger a drawWaveform async (will cache on completion)
      drawWaveform();
      return;
   }
   // Draw playhead lines
   const len = audioBuffer.length;
   const now = audioCtx.currentTime;
   const tc = window.themeColors || [];
   ctx.strokeStyle = tc[11] || '#a5a5a5';
   ctx.lineWidth = 1;
   for (const ph of activePlayheads) {
      const elapsed = now - ph.startTime;
      let samplePos = ph.offsetSamples + elapsed * audioCtx.sampleRate * ph.playbackRate;
      // Handle loop: wrap around when past loopEnd
      if (ph.loopEnabled && ph.loopEnd > ph.loopStart && samplePos >= ph.loopEnd) {
         const loopLen = ph.loopEnd - ph.loopStart;
         if (loopLen > 0) {
            samplePos = ph.loopStart + ((samplePos - ph.loopStart) % loopLen);
         }
      }
      if (samplePos < 0 || samplePos >= len) continue;
      const x = samplePos / len * w;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
   }
}

// ---- Play selection or full sample (preview from toolbar) ----
export function playSample() {
   if (!audioBuffer || !audioCtx) return;
   if (audioCtx.state === 'suspended') audioCtx.resume();
   stopSample();
   const src = audioCtx.createBufferSource();
   src.buffer = audioBuffer;
   src.connect(audioCtx.destination);
   const loop = currentSample && currentSample.loop;
   const loopEnabled = !!(loop && loop.enabled && loop.length > 2) && !selection;
   if (loopEnabled) {
      src.loop = true;
      src.loopStart = loop.start / audioBuffer.sampleRate;
      src.loopEnd = (loop.start + loop.length) / audioBuffer.sampleRate;
   }
   const offsetSamples = selection ? Math.min(selection.start, selection.end) : 0;
   if (selection) {
      const start = Math.min(selection.start, selection.end) / audioBuffer.sampleRate;
      const end = Math.max(selection.start, selection.end) / audioBuffer.sampleRate;
      src.start(0, start, end - start);
   } else {
      src.start(0);
   }
   playNode = src;
   // Track preview as a playhead too
   const ph = {
      id: ++playheadIdCounter,
      startTime: audioCtx.currentTime,
      playbackRate: 1,
      offsetSamples,
      source: src,
      isPreview: true,
      loopEnabled,
      loopStart: loop ? loop.start : 0,
      loopEnd: loop ? (loop.start + loop.length) : 0,
   };
   src.onended = () => removePlayhead(ph.id);
   activePlayheads.push(ph);
   startPlayheadRaf();
}

export function stopSample() {
   if (playNode) {
      try { playNode.stop(); } catch (_) {}
      playNode = null;
   }
   // Remove preview playheads
   activePlayheads = activePlayheads.filter(p => !p.isPreview);
   if (activePlayheads.length === 0) stopPlayheadRaf();
}

// ---- Write edited AudioBuffer back to engine sample ----
function commitBuffer(buffer) {
   if (!currentSample) return;
   const data = audioBufferToSampleData(buffer);
   currentSample.data = data;
   currentSample.length = data.length;
   audioBuffer = buffer;
   drawWaveform();
}

// ---- Edit operations ----
function getSelectionRange() {
   if (!selection || !audioBuffer) return null;
   return {
      start: Math.min(selection.start, selection.end),
      end: Math.max(selection.start, selection.end),
   };
}

function trim() {
   const range = getSelectionRange();
   if (!range) return;
   const sr = audioBuffer.sampleRate;
   const newBuf = audioCtx.createBuffer(audioBuffer.numberOfChannels, range.end - range.start, sr);
   for (let c = 0; c < audioBuffer.numberOfChannels; c++) {
      const src = audioBuffer.getChannelData(c);
      const dst = newBuf.getChannelData(c);
      for (let i = 0; i < newBuf.length; i++) dst[i] = src[range.start + i];
   }
   selection = null;
   commitBuffer(newBuf);
}

function reverse() {
   const range = getSelectionRange();
   if (range) {
      for (let c = 0; c < audioBuffer.numberOfChannels; c++) {
         const data = audioBuffer.getChannelData(c);
         const slice = data.slice(range.start, range.end);
         slice.reverse();
         for (let i = 0; i < slice.length; i++) data[range.start + i] = slice[i];
      }
   } else {
      for (let c = 0; c < audioBuffer.numberOfChannels; c++) {
         Array.prototype.reverse.call(audioBuffer.getChannelData(c));
      }
   }
   commitBuffer(audioBuffer);
}

function normalize() {
   const range = getSelectionRange();
   if (range) {
      // Normalize selection only
      let peak = 0;
      for (let c = 0; c < audioBuffer.numberOfChannels; c++) {
         const data = audioBuffer.getChannelData(c);
         for (let i = range.start; i < range.end; i++) {
            const v = Math.abs(data[i]);
            if (v > peak) peak = v;
         }
      }
      if (peak > 0) {
         const gain = 0.9 / peak;
         for (let c = 0; c < audioBuffer.numberOfChannels; c++) {
            const data = audioBuffer.getChannelData(c);
            for (let i = range.start; i < range.end; i++) data[i] *= gain;
         }
      }
   } else {
      // Normalize whole buffer
      let peak = 0;
      for (let c = 0; c < audioBuffer.numberOfChannels; c++) {
         const data = audioBuffer.getChannelData(c);
         for (let i = 0; i < data.length; i++) {
            const v = Math.abs(data[i]);
            if (v > peak) peak = v;
         }
      }
      if (peak > 0) {
         const gain = 0.9 / peak;
         for (let c = 0; c < audioBuffer.numberOfChannels; c++) {
            const data = audioBuffer.getChannelData(c);
            for (let i = 0; i < data.length; i++) data[i] *= gain;
         }
      }
   }
   commitBuffer(audioBuffer);
}

function fade(isFadeIn) {
   const range = getSelectionRange();
   const len = audioBuffer.length;
   const start = range ? range.start : 0;
   const end = range ? range.end : len;
   const dur = end - start;
   for (let c = 0; c < audioBuffer.numberOfChannels; c++) {
      const data = audioBuffer.getChannelData(c);
      for (let i = start; i < end; i++) {
         const t = (i - start) / dur;
         const factor = isFadeIn ? t : (1 - t);
         data[i] *= factor;
      }
   }
   commitBuffer(audioBuffer);
}

function silence() {
   const range = getSelectionRange();
   if (!range) return;
   for (let c = 0; c < audioBuffer.numberOfChannels; c++) {
      const data = audioBuffer.getChannelData(c);
      for (let i = range.start; i < range.end; i++) data[i] = 0;
   }
   commitBuffer(audioBuffer);
}

function applyGain(gain) {
   for (let c = 0; c < audioBuffer.numberOfChannels; c++) {
      const data = audioBuffer.getChannelData(c);
      for (let i = 0; i < data.length; i++) data[i] *= gain;
   }
   commitBuffer(audioBuffer);
}

function generateSilence(durationSec) {
   const sr = audioBuffer ? audioBuffer.sampleRate : (audioCtx ? audioCtx.sampleRate : 44100);
   const len = Math.floor(durationSec * sr);
   const buf = audioCtx.createBuffer(1, len, sr);
   commitBuffer(buf);
}

function generateTone_(waveform, freq, durationSec, level) {
   const sr = audioCtx ? audioCtx.sampleRate : 44100;
   const len = Math.floor(durationSec * sr);
   const buf = audioCtx.createBuffer(1, len, sr);
   const data = buf.getChannelData(0);
   const w = waveform || 'sine';
   for (let i = 0; i < len; i++) {
      const t = i / sr;
      const phase = 2 * Math.PI * freq * t;
      let v;
      if (w === 'sine') v = Math.sin(phase);
      else if (w === 'square') v = Math.sin(phase) > 0 ? 1 : -1;
      else if (w === 'saw') v = 2 * (freq * t - Math.floor(freq * t + 0.5));
      else if (w === 'triangle') v = 2 * Math.abs(2 * (freq * t - Math.floor(freq * t + 0.5))) - 1;
      else v = Math.sin(phase);
      data[i] = v * (level || 0.5);
   }
   commitBuffer(buf);
}

// ---- Cascading context menu (like wavedit) ----
let openMenus = [];

function closeAllMenus() {
   openMenus.forEach(m => m.remove());
   openMenus = [];
}

function buildMenuItem(label, opts = {}) {
   const el = document.createElement('div');
   el.className = 'we-ctxmenu__item';
   if (opts.disabled) el.classList.add('we-ctxmenu__item--disabled');
   if (opts.hasSubmenu) el.classList.add('we-ctxmenu__item--has-submenu');
   el.textContent = label;
   if (opts.shortcut) {
      const sc = document.createElement('span');
      sc.className = 'we-ctxmenu__shortcut';
      sc.textContent = opts.shortcut;
      el.appendChild(sc);
   }
   return el;
}

function buildSeparator() {
   const el = document.createElement('div');
   el.className = 'we-ctxmenu__item we-ctxmenu__item--separator';
   return el;
}

function positionMenu(menuEl, x, y) {
   document.body.appendChild(menuEl);
   const rect = menuEl.getBoundingClientRect();
   let left = x, top = y;
   if (left + rect.width > window.innerWidth) left = Math.max(0, window.innerWidth - rect.width - 4);
   if (top + rect.height > window.innerHeight) top = Math.max(0, window.innerHeight - rect.height - 4);
   menuEl.style.left = `${left}px`;
   menuEl.style.top = `${top}px`;
}

function openMenu(entries, x, y, parentItem) {
   if (parentItem) {
      const depth = parentItem.__depth + 1;
      openMenus = openMenus.filter(m => {
         if (m.__depth >= depth) { m.remove(); return false; }
         return true;
      });
   } else {
      closeAllMenus();
   }
   const menuEl = document.createElement('div');
   menuEl.className = 'we-ctxmenu';
   menuEl.__depth = parentItem ? parentItem.__depth + 1 : 0;
   const scroll = document.createElement('div');
   scroll.className = 'we-ctxmenu__scroll';
   menuEl.appendChild(scroll);
   entries.forEach(entry => {
      if (entry.separator) { scroll.appendChild(buildSeparator()); return; }
      const itemEl = buildMenuItem(entry.label, {
         disabled: entry.disabled,
         hasSubmenu: !!entry.submenuFn,
         shortcut: entry.shortcut,
      });
      itemEl.__depth = menuEl.__depth;
      itemEl.__entry = entry;
      scroll.appendChild(itemEl);
      if (entry.submenuFn) {
         itemEl.addEventListener('mouseenter', () => {
            openMenus = openMenus.filter(m => {
               if (m.__depth >= menuEl.__depth + 1) { m.remove(); return false; }
               return true;
            });
            Array.from(scroll.children).forEach(c => c.classList.remove('is-open'));
            itemEl.classList.add('is-open');
            const rect = itemEl.getBoundingClientRect();
            openMenu(entry.submenuFn(), rect.right - 2, rect.top, itemEl);
         });
      } else if (!entry.disabled && typeof entry.callback === 'function') {
         itemEl.addEventListener('click', () => {
            closeAllMenus();
            Promise.resolve().then(() => entry.callback()).catch(() => {});
         });
      }
   });
   positionMenu(menuEl, x, y);
   openMenus.push(menuEl);
   return menuEl;
}

// ---- Menu data (categories: Edit / Generate / Effects / Loop / File) ----
function buildEditSubmenu() {
   const hasSel = !!selection;
   const scope = hasSel ? 'selection' : 'whole';
   return [
      { label: 'Trim to selection', disabled: !hasSel, callback: trim },
      { label: 'Silence ' + scope, disabled: !hasSel, callback: silence },
      { separator: true },
      { label: 'Fade In (' + scope + ')', callback: () => fade(true) },
      { label: 'Fade Out (' + scope + ')', callback: () => fade(false) },
      { separator: true },
      { label: 'Normalize (' + scope + ')', callback: normalize },
      { label: 'Reverse (' + scope + ')', callback: reverse },
      { separator: true },
      { label: 'Clear selection', disabled: !hasSel, callback: () => { selection = null; drawWaveform(); } },
   ];
}

function buildGenerateSubmenu() {
   return [
      { label: 'Tone (sine/square/saw/tri)...', callback: promptGenerate },
      { label: 'Silence...', callback: promptSilence },
      { label: 'Noise (white)...', callback: () => generateNoise('white') },
      { label: 'Noise (pink)...', callback: () => generateNoise('pink') },
   ];
}

function buildEffectsSubmenu() {
   const hasSel = !!selection;
   const scope = hasSel ? ' (sel)' : ' (all)';
   const s = (label) => label + scope;
   return [
      { label: s('Fade In'), callback: () => applyFFEffect('afade=t=in:st=0:d=' + (audioBuffer ? audioBuffer.duration : 1)) },
      { label: s('Fade Out'), callback: () => applyFFEffect('afade=t=out:st=0:d=' + (audioBuffer ? audioBuffer.duration : 1)) },
      { label: s('Fade In/Out'), callback: () => {
         const d = audioBuffer ? audioBuffer.duration : 1;
         applyFFEffect(`afade=t=in:st=0:d=${d},afade=t=out:st=0:d=${d}`);
      }},
      { separator: true },
      { label: s('Gain'), callback: async () => {
         const v = await awaitconfig({ title: 'Gain', fields: [{ label: 'gain', type: 'number', value: 1, min: 0, max: 10, step: 0.01 }] });
         if (!v) return; applyFFEffect(`volume=${v.gain}`);
      }},
      { label: s('Normalize'), callback: () => normalize() },
      { label: s('Reverse'), callback: () => applyFFEffect('areverse') },
      { separator: true },
      { label: s('Low Pass Filter'), callback: async () => {
         const v = await awaitconfig({ title: 'Low Pass', fields: [{ label: 'cutoff', type: 'number', value: 1000, min: 20, max: 20000, step: 1 }] });
         if (!v) return; applyFFEffect(`lowpass=f=${v.cutoff}`);
      }},
      { label: s('High Pass Filter'), callback: async () => {
         const v = await awaitconfig({ title: 'High Pass', fields: [{ label: 'cutoff', type: 'number', value: 500, min: 20, max: 20000, step: 1 }] });
         if (!v) return; applyFFEffect(`highpass=f=${v.cutoff}`);
      }},
      { label: s('Band Pass Filter'), callback: async () => {
         const v = await awaitconfig({ title: 'Band Pass', fields: [
            { label: 'low_freq', type: 'number', value: 200, min: 20, max: 20000, step: 1 },
            { label: 'high_freq', type: 'number', value: 2000, min: 20, max: 20000, step: 1 },
         ]});
         if (!v) return; applyFFEffect(`highpass=f=${v.low_freq},lowpass=f=${v.high_freq}`);
      }},
      { separator: true },
      { label: s('Bit Crusher'), callback: async () => {
         const v = await awaitconfig({ title: 'Bit Crusher', fields: [
            { label: 'bits', type: 'number', value: 8, min: 1, max: 16, step: 1 },
            { label: 'reduction', type: 'number', value: 0.5, min: 0, max: 1, step: 0.01 },
         ]});
         if (!v) return; applyFFEffect(`acrusher=bits=${v.bits}:level_in=1:level_out=1:mode=log:aa=1:samples=1`);
      }},
      { label: s('Distortion'), callback: async () => {
         const v = await awaitconfig({ title: 'Distortion', fields: [{ label: 'threshold', type: 'number', value: 0.7, min: 0, max: 1, step: 0.01 }] });
         if (!v) return; applyFFEffect(`asoftclip=type=tanh:threshold=${v.threshold}`);
      }},
      { label: s('Soft Clipper'), callback: async () => {
         const v = await awaitconfig({ title: 'Soft Clipper', fields: [{ label: 'threshold', type: 'number', value: 0.5, min: 0, max: 1, step: 0.01 }] });
         if (!v) return; applyFFEffect(`asoftclip=type=cubic:threshold=${v.threshold}`);
      }},
      { separator: true },
      { label: s('Delay/Echo'), callback: async () => {
         const v = await awaitconfig({ title: 'Echo', fields: [
            { label: 'in_gain', type: 'number', value: 0.6, min: 0, max: 1, step: 0.01 },
            { label: 'out_gain', type: 'number', value: 0.3, min: 0, max: 1, step: 0.01 },
            { label: 'delay_ms', type: 'number', value: 200, min: 1, max: 5000, step: 1 },
            { label: 'decay', type: 'number', value: 0.5, min: 0, max: 1, step: 0.01 },
         ]});
         if (!v) return; applyFFEffect(`aecho=${v.in_gain}:${v.out_gain}:${v.delay_ms}:${v.decay}`);
      }},
      { label: s('Reverb'), callback: async () => {
         const v = await awaitconfig({ title: 'Reverb', fields: [
            { label: 'in_gain', type: 'number', value: 0.8, min: 0, max: 1, step: 0.01 },
            { label: 'out_gain', type: 'number', value: 0.9, min: 0, max: 1, step: 0.01 },
            { label: 'delay_ms', type: 'number', value: 80, min: 1, max: 1000, step: 1 },
            { label: 'decay', type: 'number', value: 0.3, min: 0, max: 1, step: 0.01 },
         ]});
         if (!v) return; applyFFEffect(`aecho=${v.in_gain}:${v.out_gain}:${v.delay_ms}:${v.decay}`);
      }},
      { label: s('Chorus'), callback: async () => {
         const v = await awaitconfig({ title: 'Chorus', fields: [
            { label: 'in_gain', type: 'number', value: 0.5, min: 0, max: 2, step: 0.01 },
            { label: 'out_gain', type: 'number', value: 0.9, min: 0, max: 2, step: 0.01 },
            { label: 'delays', type: 'number', value: 1, min: 0, max: 200, step: 1 },
            { label: 'decays', type: 'number', value: 0.4, min: 0, max: 2, step: 0.01 },
            { label: 'speeds', type: 'number', value: 1, min: 0, max: 20, step: 0.01 },
            { label: 'depths', type: 'number', value: 1, min: 0, max: 20, step: 0.01 },
         ]});
         if (!v) return; applyFFEffect(`chorus=in_gain=${v.in_gain}:out_gain=${v.out_gain}:delays=${v.delays}:decays=${v.decays}:speeds=${v.speeds}:depths=${v.depths}`);
      }},
      { label: s('Flanger'), callback: async () => {
         const v = await awaitconfig({ title: 'Flanger', fields: [
            { label: 'delay', type: 'number', value: 5, min: 1, max: 30, step: 1 },
            { label: 'depth', type: 'number', value: 2, min: 0, max: 10, step: 1 },
            { label: 'speed', type: 'number', value: 0.5, min: 0.1, max: 10, step: 0.1 },
         ]});
         if (!v) return; applyFFEffect(`flanger=delay=${v.delay}:depth=${v.depth}:speed=${v.speed}`);
      }},
      { label: s('Phaser'), callback: async () => {
         const v = await awaitconfig({ title: 'Phaser', fields: [
            { label: 'in_gain', type: 'number', value: 0.4, min: 0, max: 2, step: 0.01 },
            { label: 'out_gain', type: 'number', value: 0.74, min: 0, max: 2, step: 0.01 },
            { label: 'delay', type: 'number', value: 3, min: 0, max: 20, step: 0.01 },
            { label: 'decay', type: 'number', value: 0.4, min: 0, max: 2, step: 0.01 },
            { label: 'speed', type: 'number', value: 0.5, min: 0, max: 20, step: 0.01 },
         ]});
         if (!v) return; applyFFEffect(`aphaser=in_gain=${v.in_gain}:out_gain=${v.out_gain}:delay=${v.delay}:decay=${v.decay}:speed=${v.speed}`);
      }},
      { separator: true },
      { label: s('Tremolo'), callback: async () => {
         const v = await awaitconfig({ title: 'Tremolo', fields: [
            { label: 'frequency', type: 'number', value: 10, min: 0, max: 100, step: 1 },
            { label: 'depth', type: 'number', value: 0.7, min: 0, max: 1, step: 0.01 },
         ]});
         if (!v) return; applyFFEffect(`tremolo=f=${v.frequency}:d=${v.depth}`);
      }},
      { label: s('Vibrato'), callback: async () => {
         const v = await awaitconfig({ title: 'Vibrato', fields: [
            { label: 'frequency', type: 'number', value: 10, min: 0, max: 100, step: 1 },
            { label: 'depth', type: 'number', value: 0.5, min: 0, max: 1, step: 0.01 },
         ]});
         if (!v) return; applyFFEffect(`vibrato=f=${v.frequency}:d=${v.depth}`);
      }},
      { label: s('Pan'), callback: async () => {
         const v = await awaitconfig({ title: 'Pan', fields: [{ label: 'pan', type: 'number', value: 0, min: -1, max: 1, step: 0.01 }] });
         if (!v) return; applyFFEffect(`pan=stereo|c0=${1 - v.pan}|c1=${1 + v.pan}`);
      }},
      { separator: true },
      { label: s('Compressor'), callback: async () => {
         const v = await awaitconfig({ title: 'Compressor', fields: [
            { label: 'threshold', type: 'number', value: -20, min: -60, max: 0, step: 1 },
            { label: 'ratio', type: 'number', value: 4, min: 1, max: 20, step: 0.1 },
            { label: 'attack', type: 'number', value: 200, min: 1, max: 5000, step: 1 },
            { label: 'release', type: 'number', value: 250, min: 1, max: 5000, step: 1 },
         ]});
         if (!v) return; applyFFEffect(`acompressor=threshold=${v.threshold}dB:ratio=${v.ratio}:attack=${v.attack}:release=${v.release}`);
      }},
      { label: s('Limiter'), callback: async () => {
         const v = await awaitconfig({ title: 'Limiter', fields: [
            { label: 'limit', type: 'number', value: 0.9, min: 0, max: 1, step: 0.01 },
            { label: 'attack', type: 'number', value: 5, min: 1, max: 100, step: 1 },
            { label: 'release', type: 'number', value: 50, min: 1, max: 1000, step: 1 },
         ]});
         if (!v) return; applyFFEffect(`alimiter=limit=${v.limit}:attack=${v.attack}:release=${v.release}`);
      }},
      { label: s('Gate'), callback: async () => {
         const v = await awaitconfig({ title: 'Noise Gate', fields: [
            { label: 'threshold', type: 'number', value: -40, min: -80, max: 0, step: 1 },
            { label: 'ratio', type: 'number', value: 2, min: 1, max: 20, step: 0.1 },
            { label: 'attack', type: 'number', value: 20, min: 1, max: 5000, step: 1 },
            { label: 'release', type: 'number', value: 250, min: 1, max: 5000, step: 1 },
         ]});
         if (!v) return; applyFFEffect(`agate=threshold=${v.threshold}dB:ratio=${v.ratio}:attack=${v.attack}:release=${v.release}`);
      }},
      { separator: true },
      { label: s('Equalizer'), callback: async () => {
         const v = await awaitconfig({ title: 'Equalizer', fields: [
            { label: 'frequency', type: 'number', value: 1000, min: 20, max: 20000, step: 1 },
            { label: 'width', type: 'number', value: 1, min: 0.1, max: 10, step: 0.1 },
            { label: 'gain', type: 'number', value: 3, min: -30, max: 30, step: 0.1 },
         ]});
         if (!v) return; applyFFEffect(`equalizer=f=${v.frequency}:width_type=o:width=${v.width}:g=${v.gain}`);
      }},
      { label: s('Bass Boost'), callback: async () => {
         const v = await awaitconfig({ title: 'Bass Boost', fields: [
            { label: 'gain', type: 'number', value: 10, min: -30, max: 30, step: 0.1 },
            { label: 'frequency', type: 'number', value: 100, min: 20, max: 1000, step: 1 },
         ]});
         if (!v) return; applyFFEffect(`bass=g=${v.gain}:f=${v.frequency}`);
      }},
      { label: s('Treble Boost'), callback: async () => {
         const v = await awaitconfig({ title: 'Treble Boost', fields: [
            { label: 'gain', type: 'number', value: 10, min: -30, max: 30, step: 0.1 },
            { label: 'frequency', type: 'number', value: 3000, min: 1000, max: 20000, step: 1 },
         ]});
         if (!v) return; applyFFEffect(`treble=g=${v.gain}:f=${v.frequency}`);
      }},
      { separator: true },
      { label: s('Pitch Shift'), callback: async () => {
         const v = await awaitconfig({ title: 'Pitch Shift', fields: [{ label: 'semitones', type: 'number', value: 2, min: -24, max: 24, step: 0.5 }] });
         if (!v) return;
         const ratio = Math.pow(2, v.semitones / 12);
         const sr = audioCtx.sampleRate;
         applyFFEffect(`asetrate=${Math.floor(sr * ratio)},aresample=${sr},atempo=${(1 / ratio).toFixed(6)}`);
      }},
      { label: s('Speed (stretch)'), callback: async () => {
         const v = await awaitconfig({ title: 'Speed (stretch)', fields: [{ label: 'speed', type: 'number', value: 1.5, min: 0.25, max: 4, step: 0.05 }] });
         if (!v) return; applyFFEffect(`atempo=${v.speed}`);
      }},
      { label: s('Speed (pitch)'), callback: async () => {
         const v = await awaitconfig({ title: 'Speed (pitch)', fields: [{ label: 'speed', type: 'number', value: 1.5, min: 0.25, max: 4, step: 0.05 }] });
         if (!v) return; applyFFEffect(`asetrate=${Math.floor(audioCtx.sampleRate * v.speed)}`);
      }},
      { separator: true },
      { label: s('Telephone'), callback: () => applyFFEffect('highpass=f=300,lowpass=f=3400') },
      { label: s('DC Shift'), callback: async () => {
         const v = await awaitconfig({ title: 'DC Shift', fields: [{ label: 'shift', type: 'number', value: 0.1, min: -1, max: 1, step: 0.01 }] });
         if (!v) return; applyFFEffect(`dcshift=${v.shift}`);
      }},
      { label: s('Frequency Shift'), callback: async () => {
         const v = await awaitconfig({ title: 'Frequency Shift', fields: [{ label: 'shift', type: 'number', value: 440, min: -44100, max: 44100, step: 1 }] });
         if (!v) return; applyFFEffect(`afreqshift=shift=${v.shift}`);
      }},
   ];
}

function buildLoopSubmenu() {
   const hasLoop = !!(currentSample && currentSample.loop && currentSample.loop.enabled);
   return [
      { label: hasLoop ? 'Disable loop' : 'Enable loop', callback: toggleLoop },
      { label: 'Set loop to selection', disabled: !selection, callback: setLoopToSelection },
      { label: 'Set loop to entire sample', callback: setLoopToAll },
   ];
}

function buildFileSubmenu() {
   return [
      { label: 'Export as WAV', disabled: !audioBuffer, callback: exportWav },
   ];
}

function buildRootEntries() {
   return [
      { label: 'Edit', submenuFn: buildEditSubmenu },
      { label: 'Generate', submenuFn: buildGenerateSubmenu },
      { label: 'Effects', submenuFn: buildEffectsSubmenu },
      { label: 'Loop', submenuFn: buildLoopSubmenu },
      { label: 'File', submenuFn: buildFileSubmenu },
   ];
}

function showContextMenu(x, y) {
   openMenu(buildRootEntries(), x, y, null);
}

// Close menus on outside click / escape
document.addEventListener('mousedown', (e) => {
   if (openMenus.length === 0) return;
   if (openMenus.some(m => m.contains(e.target))) return;
   closeAllMenus();
});
document.addEventListener('keydown', (e) => {
   if (e.key === 'Escape' && openMenus.length > 0) closeAllMenus();
});

// ---- Effect config modal (replaces native prompt) ----
function awaitconfig(config) {
   return new Promise((resolve) => {
      const modal = document.createElement('div');
      modal.className = 'xt-se-modal';
      const form = document.createElement('div');
      form.className = 'xt-se-form';
      modal.appendChild(form);

      const title = document.createElement('h1');
      title.textContent = config.title;
      form.appendChild(title);

      const inputs = [];
      (config.fields || []).forEach(field => {
         const div = document.createElement('div');
         div.className = 'xt-se-field';
         const label = document.createElement('label');
         label.textContent = field.label;
         div.appendChild(label);

         if (field.type === 'select') {
            const sel = document.createElement('select');
            (field.options || []).forEach(opt => {
               const o = document.createElement('option');
               o.value = opt.value;
               o.textContent = opt.label;
               sel.appendChild(o);
            });
            sel.value = field.value || '';
            div.appendChild(sel);
            inputs.push({ key: field.label, el: sel, type: 'select' });
         } else if (field.type === 'boolean') {
            const cb = document.createElement('input');
            cb.type = 'checkbox';
            cb.checked = !!field.value;
            div.appendChild(cb);
            inputs.push({ key: field.label, el: cb, type: 'boolean' });
         } else {
            const inp = document.createElement('input');
            inp.type = field.type || (typeof field.value === 'number' ? 'number' : 'text');
            inp.value = field.value !== undefined ? field.value : '';
            if (field.min !== undefined) inp.min = field.min;
            if (field.max !== undefined) inp.max = field.max;
            if (field.step !== undefined) inp.step = field.step;
            div.appendChild(inp);
            inputs.push({ key: field.label, el: inp, type: inp.type });
         }
         form.appendChild(div);
      });

      const btns = document.createElement('div');
      btns.className = 'xt-se-form-buttons';
      const submit = document.createElement('button');
      submit.className = 'xt-se-submit';
      submit.textContent = 'ok';
      const cancel = document.createElement('button');
      cancel.textContent = 'cancel';
      btns.appendChild(cancel);
      btns.appendChild(submit);
      form.appendChild(btns);

      function close(val) {
         modal.remove();
         resolve(val);
      }

      submit.addEventListener('click', () => {
         const values = {};
         inputs.forEach(inp => {
            if (inp.type === 'boolean') values[inp.key] = inp.el.checked;
            else if (inp.type === 'number') {
               const n = parseFloat(inp.el.value);
               const field = (config.fields || []).find(f => f.label === inp.key);
               const lo = field && field.min !== undefined ? field.min : -Infinity;
               const hi = field && field.max !== undefined ? field.max : Infinity;
               values[inp.key] = Math.max(lo, Math.min(hi, isNaN(n) ? 0 : n));
            } else {
               values[inp.key] = inp.el.value;
            }
         });
         close(values);
      });
      cancel.addEventListener('click', () => close(null));
      modal.addEventListener('click', (e) => { if (e.target === modal) close(null); });

      document.body.appendChild(modal);
      // Focus first input
      if (inputs.length > 0) {
         const first = inputs[0].el;
         if (first.focus) { first.focus(); if (first.select) first.select(); }
      }
   });
}

async function generateNoise(type) {
   const values = await awaitconfig({
      title: type === 'pink' ? 'Pink Noise' : 'White Noise',
      fields: [{ label: 'duration', type: 'number', value: 0.5, min: 0.01, max: 60, step: 0.1 }],
   });
   if (!values) return;
   const d = values.duration;
   const sr = audioCtx ? audioCtx.sampleRate : 44100;
   const len = Math.floor(d * sr);
   const buf = audioCtx.createBuffer(1, len, sr);
   const data = buf.getChannelData(0);
   if (type === 'pink') {
      // Simple pink noise approximation
      let b0 = 0, b1 = 0, b2 = 0;
      for (let i = 0; i < len; i++) {
         const white = Math.random() * 2 - 1;
         b0 = 0.99765 * b0 + white * 0.0990460;
         b1 = 0.96300 * b1 + white * 0.2965164;
         b2 = 0.57000 * b2 + white * 1.0526913;
         data[i] = (b0 + b1 + b2 + white * 0.1848) * 0.15;
      }
   } else {
      for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
   }
   commitBuffer(buf);
}

function setLoopToSelection() {
   if (!selection || !currentSample || !audioBuffer) return;
   if (!currentSample.loop) currentSample.loop = { enabled: false, start: 0, length: 0 };
   currentSample.loop.start = Math.min(selection.start, selection.end);
   currentSample.loop.length = Math.abs(selection.end - selection.start);
   currentSample.loop.enabled = true;
   syncLoopUI();
   drawWaveform();
}

function setLoopToAll() {
   if (!currentSample || !audioBuffer) return;
   if (!currentSample.loop) currentSample.loop = { enabled: false, start: 0, length: 0 };
   currentSample.loop.start = 0;
   currentSample.loop.length = audioBuffer.length;
   currentSample.loop.enabled = true;
   syncLoopUI();
   drawWaveform();
}

function toggleLoop() {
   if (!currentSample) return;
   if (!currentSample.loop) currentSample.loop = { enabled: false, start: 0, length: 0 };
   currentSample.loop.enabled = !currentSample.loop.enabled;
   if (currentSample.loop.enabled && (!currentSample.loop.length) && audioBuffer) {
      currentSample.loop.start = 0;
      currentSample.loop.length = audioBuffer.length;
   }
   syncLoopUI();
   drawWaveform();
}

async function promptGenerate() {
   const values = await awaitconfig({
      title: 'Generate Tone',
      fields: [
         { label: 'waveform', type: 'select', value: 'sine', options: [
            { value: 'sine', label: 'sine' },
            { value: 'square', label: 'square' },
            { value: 'saw', label: 'saw' },
            { value: 'triangle', label: 'triangle' },
         ]},
         { label: 'frequency', type: 'number', value: 440, min: 1, max: 20000, step: 1 },
         { label: 'duration', type: 'number', value: 1, min: 0.01, max: 60, step: 0.1 },
         { label: 'level', type: 'number', value: 0.5, min: 0, max: 1, step: 0.01 },
      ],
   });
   if (!values) return;
   generateTone_(values.waveform, values.frequency, values.duration, values.level);
}

async function promptSilence() {
   const values = await awaitconfig({
      title: 'Generate Silence',
      fields: [{ label: 'duration', type: 'number', value: 0.5, min: 0.01, max: 60, step: 0.1 }],
   });
   if (!values) return;
   generateSilence(values.duration);
}

function exportWav() {
   if (!audioBuffer) return;
   const wav = audioBufferToWavInternal(audioBuffer);
   const blob = new Blob([wav], { type: 'audio/wav' });
   const url = URL.createObjectURL(blob);
   const a = document.createElement('a');
   a.href = url;
   a.download = (currentSample && currentSample.name ? currentSample.name : 'sample') + '.wav';
   a.click();
   setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function audioBufferToWavInternal(buffer) {
   const numCh = buffer.numberOfChannels;
   const sr = buffer.sampleRate;
   const len = buffer.length;
   const bytesPerSample = 2;
   const blockAlign = numCh * bytesPerSample;
   const dataSize = len * blockAlign;
   const ab = new ArrayBuffer(44 + dataSize);
   const dv = new DataView(ab);
   const writeStr = (off, s) => { for (let i = 0; i < s.length; i++) dv.setUint8(off + i, s.charCodeAt(i)); };
   writeStr(0, 'RIFF'); dv.setUint32(4, 36 + dataSize, true); writeStr(8, 'WAVE');
   writeStr(12, 'fmt '); dv.setUint32(16, 16, true); dv.setUint16(20, 1, true);
   dv.setUint16(22, numCh, true); dv.setUint32(24, sr, true);
   dv.setUint32(28, sr * blockAlign, true); dv.setUint16(32, blockAlign, true);
   dv.setUint16(34, 16, true); writeStr(36, 'data'); dv.setUint32(40, dataSize, true);
   let off = 44;
   for (let i = 0; i < len; i++) {
      for (let c = 0; c < numCh; c++) {
         let v = Math.max(-1, Math.min(1, buffer.getChannelData(c)[i]));
         v = v < 0 ? v * 0x8000 : v * 0x7FFF;
         dv.setInt16(off, v, true);
         off += 2;
      }
   }
   return ab;
}

// ---- Toolbar buttons (play/stop only — rest via context menu) ----
function wireToolbar() {
   const bind = (id, fn) => {
      const el = document.getElementById(id);
      if (el) el.addEventListener('click', fn);
   };
   bind('xt-se-play', playSample);
   bind('xt-se-stop', stopSample);
}

export function redraw() {
   drawWaveform();
}
